import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { FEATURE_PAGE_MAP } from "../features/featurePageMap";
import { setFeatures, normalizeFeatureCodes } from "../features/user/userSlice";
import { fetchFeed } from "../api/circleAPI";
import styles from "./Navbar.module.css";

const normalizeFeatureCode = (code = "") =>
  typeof code === "string" ? code.trim().toUpperCase() : "";

function Navbar({ onOpenChatPanel }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // const [pageTitle, setPageTitle] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState(""); // 'coming-soon' or 'upgrade-plan'
  // Only open chat by default if not on login page or certain brand internal pages
  const isLogin =
    location.pathname === "/login" ||
    location.pathname === "/signup" ||
    location.pathname === "/";
  // Removed chatOpen state, as chat panel is managed in App.js

  // Get user features from Redux
  const token = useSelector((state) => state.user.token);
  const userFeatures = useSelector((state) => state.user.features || []);
  const userFeatureCodes = userFeatures
    .map((feature) => normalizeFeatureCode(feature?.code))
    .filter(Boolean);
  const accessibleFeatureSet = new Set(userFeatureCodes);
  const user_type = useSelector((state) => state.user.user_type);
  const normalizedUserType = normalizeFeatureCode(user_type);

  // Define all available features to show in navbar
  const allFeatures = [
    "GROWTH_SUITE",
    "BRAND_DASHBOARD",
    "SUSPIRE_TERMINAL",
    "SUSPIRE_CIRCLE",
    "SUPPORT_TICKETS",
    "SSU_BASIC",
    "SUSPIRE_SUPPORT_NETWORK",
    "3SC_CERTIFICATION",
  ];

  // Feature flags: only these routes are live even if a brand has additional entitlements
  const currentlyAccessible = [
    "GROWTH_SUITE",
    "3SC_CERTIFICATION",
    "BRAND_DASHBOARD",
    "SUSPIRE_SUPPORT_NETWORK",
    "SUSPIRE_TERMINAL",
    "EDIT_ONBOARDING_FORM",
    "SUSPIRE_CIRCLE",
    "SUPPORT_TICKETS",
  ];

  const hasSyncedFeaturesForToken = useRef(null);
  const [circleHasNew, setCircleHasNew] = useState(false);
  const circleCheckTimestamp = useRef(0);
  // Token (login identity) the dot was last checked for; lets a new login bypass
  // the throttle and guards against applying a stale feed response after a switch.
  const lastCheckedCircleToken = useRef(null);
  // Monotonic id of the most recent in-flight check, so a late/out-of-order
  // response (e.g. rapid enter/leave of the Circle page) can't clobber a newer one.
  const circleRequestId = useRef(0);

  useEffect(() => {
    if (!token) {
      hasSyncedFeaturesForToken.current = null;
      return;
    }

    if (hasSyncedFeaturesForToken.current === token) {
      return;
    }

    let isCancelled = false;

    const syncAccessibleFeatures = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/auth/users/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!res.ok) {
          if (res.status === 401 || res.status === 403) {
            hasSyncedFeaturesForToken.current = null;
          }
          return;
        }

        const data = await res.json();
        if (isCancelled) return;

        const accessibleFeatures = data?.user?.accessible_features || [];
        const normalized = normalizeFeatureCodes(accessibleFeatures);
        dispatch(setFeatures(normalized));
        hasSyncedFeaturesForToken.current = token;
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("Navbar: Failed to sync accessible features", err);
        }
        hasSyncedFeaturesForToken.current = null;
      }
    };

    syncAccessibleFeatures();

    return () => {
      isCancelled = true;
    };
  }, [token, dispatch]);

  // Check Suspire Circle for recent admin posts (brand users only).
  // Runs on login/mount + whenever the user navigates away from the Circle page
  // (so the dot clears immediately after a brand visits /suspire-circle).
  useEffect(() => {
    if (normalizedUserType !== 'USER' || !token) {
      setCircleHasNew(false);
      // Forget the checked identity so the next login always re-checks.
      lastCheckedCircleToken.current = null;
      return;
    }

    // A new login (token change) and leaving the Circle page always force a fresh
    // check; routine navigation is throttled to once / 2 min. The new-login bypass
    // matters because AuthRedirector navigates immediately after login: that
    // navigation lands inside the throttle window, so without the bypass the only
    // check would be skipped and the dot would surface minutes late.
    const isNewLogin = lastCheckedCircleToken.current !== token;
    const justLeftCircle = circleCheckTimestamp.current === -1;
    const now = Date.now();
    if (!isNewLogin && !justLeftCircle && now - circleCheckTimestamp.current < 120000) return;
    circleCheckTimestamp.current = now;
    lastCheckedCircleToken.current = token;
    const requestId = ++circleRequestId.current;

    const THREE_DAYS = 3 * 24 * 60 * 60 * 1000;
    const cutoff = now - THREE_DAYS;

    fetchFeed(token, { page: 1, pageSize: 20 })
      .then(data => {
        // Drop this response if a newer check has since started (out-of-order
        // resolution, e.g. rapid enter/leave of the Circle page) or the user has
        // logged out / switched accounts. We intentionally don't cancel on
        // cleanup: the post-login navigation re-runs this effect, and cancelling
        // there would drop the only fetch.
        if (requestId !== circleRequestId.current || lastCheckedCircleToken.current !== token) return;
        // Show dot when any admin (Suspire) post was published within the last 3 days.
        // Time-based only — independent of when the brand last logged in.
        const hasNew = data.results.some(
          p => p.author && p.author.type === 'suspire' &&
               new Date(p.created_at).getTime() > cutoff
        );
        setCircleHasNew(hasNew);
      })
      .catch(() => {});
  }, [token, normalizedUserType, location.pathname]);

  // When brand leaves /suspire-circle, mark timestamp as -1 so next effect run forces re-check
  useEffect(() => {
    return () => {
      if (location.pathname === '/suspire-circle' && normalizedUserType === 'USER') {
        circleCheckTimestamp.current = -1;
      }
    };
  }, [location.pathname, normalizedUserType]);

  // Build navbar items based on user type
  let navbarItems = [];
  // For appuser, show all features
  navbarItems = allFeatures
    .map((featureCode) => {
      const normalizedCode = normalizeFeatureCode(featureCode);
      const featureConfig = FEATURE_PAGE_MAP[normalizedCode];
      if (!featureConfig || !featureConfig.route) {
        return null;
      }
      const inUserPlan = accessibleFeatureSet.has(normalizedCode);
      const isFeatureLive = currentlyAccessible.includes(normalizedCode);

      // Feature access override — treat these as core features always accessible
      const hasAccess =
        normalizedCode === "EDIT_ONBOARDING_FORM" ||
        normalizedCode === "SSU_BASIC" ||
        normalizedCode === "SUPPORT_TICKETS" ||
        normalizedCode === "SUSPIRE_CIRCLE"
          ? true
          : inUserPlan && isFeatureLive;

      return {
        ...featureConfig,
        featureCode: normalizedCode,
        hasAccess,
        inUserPlan,
        isFeatureLive,
      };
    })
    .filter(Boolean);

  const onboarding = useSelector((state) => state.user.onboarding);

  useEffect(() => {
    // Map pathnames to page titles
    const pageMap = {
      "/profile": "Profile",
      "/brand-dashboard": "Brand Dashboard",
      "/forum": "Community Forum",
      "/seller-university": "Seller University",
      "/suspire-support-network": "Suspire Support Network",
      "/sustainability": "Sustainability Questionnaire",
      "/certificate": "3SC Certificate",
      "/calendar": "Brand Visibility Calendar",
      "/suspire-visibility": "Suspire Visibility Calendar",
      "/customer-crm": "Customer CRM",
      "/catalog": "Product Catalog",
      "/logout": "Logout",
      "/login": "Login",
      "/": "Login",
      "/brand-genai": "Brand Listing Page",
      "/brand-email-demo": "Demo Performance Email",
      "/brand-genai-marketing": "Marketing Automation",
      "/growth-suite": "Growth Suite",
      "/brands": "Brand Management",
      "/brand-product-content": "Product Details",
      "/brand-email-calendar": "Performance Email Calendar",
      "/ai-banner-suggestions": "AI Banner Suggestions",
      "/suspire-circle": "Suspire Circle",
      "/subscriptions": "Subscriptions",
      "/pricing": "Subscriptions", // For backward compatibility
      "/suspire-terminal": "Suspire Terminal",
    };
    // Find the best matching key (longest prefix match)
    // const match = Object.keys(pageMap).find(key => location.pathname === key || location.pathname.startsWith(key + '/'));
    // setPageTitle(pageMap[match] || '');
  }, [location.pathname]);

  // Only show onboarding menu if appuser and under-review
  // NEW API FORMAT: onboarding.status === 'under-review'
  const showOnboardingMenu =
    normalizedUserType === "USER" && onboarding?.status === "under-review";

  const handleMenuClick = (e, page) => {
    if (page.hasAccess) {
      setOpen(false);
      return;
    }

    e.preventDefault();
    if (!page.isFeatureLive || page.comingSoon) {
      setModalMessage(
        "This feature is coming soon and will be available in future updates.",
      );
      setModalType("coming-soon");
    } else if (!page.inUserPlan) {
      setModalMessage("Upgrade your plan to access this premium feature.");
      setModalType("upgrade-plan");
    } else {
      setModalMessage("This feature is currently unavailable.");
      setModalType("coming-soon");
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalMessage("");
    setModalType("");
  };

  return (
    <>
      {/* <div className={styles.topbar}>
        {!isLogin && (
          <button
            className={styles.hamburger}
            aria-label="Toggle menu"
            onClick={() => setOpen((prev) => !prev)}
          >
            <span className={open ? styles.barOpen : styles.bar}></span>
            <span className={open ? styles.barOpen : styles.bar}></span>
            <span className={open ? styles.barOpen : styles.bar}></span>
          </button>
        )}
        <img
          src={suspireLogo}
          alt="Suspire Logo"
          className={styles.logo}
          style={{ height: '40px' }}
        />
        <div className={styles.pageTitle}>{pageTitle}</div>
      </div> */}

      {/* New Premium Sidebar for authenticated users */}
      {!isLogin && (
        <aside className={styles.premiumSidebar}>
          <nav>
            <ul className={styles.sidebarNav}>
              {navbarItems.map((item, index) => {
                const iconMap = {
                  GROWTH_SUITE: "/images/navbar-icons/nav_GrowthSuite.png",
                  BRAND_DASHBOARD: "/images/navbar-icons/nav_BrandDashboard.png",
                  COMMUNITY_FORUM: "/images/navbar-icons/CommunityForum.svg",
                  SUSPIRE_CIRCLE:
                    "/images/navbar-icons/nav_SuspireCircle.png",
                  PRODUCT_LISTING: "/images/navbar-icons/nav_Helpdesk.png",
                  SSU_BASIC: "/images/navbar-icons/nav_SellerUniversity.png",
                  SUSPIRE_SUPPORT_NETWORK:
                    "/images/navbar-icons/nav_SupportNetwork.png",
                  "3SC_CERTIFICATION":
                    "/images/navbar-icons/nav_3SC.png",
                  BRAND_MANAGEMENT: "/images/navbar-icons/nav_BrandDashboard.png",
                  SUSPIRE_TERMINAL:
                    "/images/navbar-icons/SuspireTerminalIcon.svg",
                  EDIT_ONBOARDING_FORM:
                    "/images/navbar-icons/nav_BrandDashboard.png",
                  SUPPORT_TICKETS:
                    "/images/navbar-icons/nav_Helpdesk.png",
                };
                const iconSrc =
                  iconMap[item.featureCode] ||
                  "/images/navbar-icons/nav_GrowthSuite.png";

                // Special handling for 3SC Certification button
                if (
                  item.featureCode === "3SC_CERTIFICATION" &&
                  item.hasAccess
                ) {
                  return (
                    <li key={index} className={styles.tooltipContainer}>
                      <button
                        type="button"
                        className={`${styles.sidebarIcon} ${location.pathname === item.route ? styles.sidebarActive : ""}`}
                        style={{
                          border: "none",
                          padding: 0,
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          navigate("/sustainability");
                          setOpen(false);
                        }}
                      >
                        <img src={iconSrc} alt={`${item.label} icon`} />
                      </button>
                      <div className={styles.tooltip}>{item.label}</div>
                    </li>
                  );
                }

                // Special handling for SSU Basic button
                if (item.featureCode === "SSU_BASIC" && item.hasAccess) {
                  return (
                    <li key={index} className={styles.tooltipContainer}>
                      <button
                        type="button"
                        className={`${styles.sidebarIcon} ${location.pathname === "/ssu-chatbot" || location.pathname === "/seller-university" ? styles.sidebarActive : ""}`}
                        style={{
                          border: "none",
                          padding: 0,
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          navigate("/ssu-chatbot");
                          setOpen(false);
                        }}
                      >
                        <img src={iconSrc} alt={`${item.label} icon`} />
                      </button>
                      <div className={styles.tooltip}>{item.label}</div>
                    </li>
                  );
                }

                // Default rendering for other buttons
                const showCircleDot =
                  item.featureCode === "SUSPIRE_CIRCLE" &&
                  normalizedUserType === "USER" &&
                  circleHasNew;
                return (
                  <li key={index} className={styles.tooltipContainer}>
                    <Link
                      to={item.hasAccess ? item.route : "#"}
                      className={`${styles.sidebarIcon} ${location.pathname === item.route ? styles.sidebarActive : ""} ${!item.hasAccess ? styles.sidebarDisabled : ""}`}
                      onClick={(e) => handleMenuClick(e, item)}
                      style={{ position: "relative" }}
                    >
                      <img src={iconSrc} alt={`${item.label} icon`} />
                      {showCircleDot && (
                        <span
                          style={{
                            position: "absolute",
                            top: 2,
                            right: 2,
                            width: 9,
                            height: 9,
                            background: "#e74c3c",
                            borderRadius: "50%",
                            border: "2px solid white",
                            display: "block",
                          }}
                        />
                      )}
                    </Link>
                    <div className={styles.tooltip}>{item.label}</div>
                  </li>
                );
              })}
              {/* Subscriptions & pricing — visible to all logged-in users */}
              <li className={styles.tooltipContainer}>
                <Link
                  to="/subscriptions"
                  className={`${styles.sidebarIcon} ${
                    location.pathname === "/subscriptions" ||
                    location.pathname === "/pricing" ||
                    location.pathname === "/plans_and_pricing"
                      ? styles.sidebarActive
                      : ""
                  }`}
                  onClick={() => setOpen(false)}
                >
                  <img
                    src="/images/navbar-icons/nav_Subscriptions.png"
                    alt="subscriptions icon"
                    style={{ transform: "scale(1.25)" }}
                  />
                </Link>
                <div className={styles.tooltip}>Subscriptions</div>
              </li>

              <li className={styles.tooltipContainer}>
                <Link
                  to="/logout"
                  className={`${styles.sidebarIcon} ${location.pathname === "/logout" ? styles.sidebarActive : ""}`}
                >
                  <img
                    src="/images/navbar-icons/Logout.svg"
                    alt="logout icon"
                  />
                </Link>
                <div className={styles.tooltip}>Logout</div>
              </li>
            </ul>
          </nav>
        </aside>
      )}

      {/* Hide menu sidebar on login page */}
      {!isLogin && (
        <div
          className={open ? styles.menuOpen : styles.menuClosed}
          style={{
            maxHeight: "calc(100vh - 60px)",
            overflowY: "auto",
            paddingRight: "6px",
          }}
        >
          {/* Onboarding menu option for appuser under review */}
          {showOnboardingMenu && normalizedUserType === "USER" && (
            <Link
              to="/onboarding"
              onClick={() => setOpen(false)}
              className={
                location.pathname === "/onboarding" ? styles.active : ""
              }
              style={{ fontWeight: "bold", color: "#00bfae" }}
            >
              Onboarding
            </Link>
          )}
          {/* Show features based on user type */}
          {navbarItems.map((page, index) => {
            // Special handling for SSU Basic in mobile menu
            if (page.featureCode === "SSU_BASIC" && page.hasAccess) {
              return (
                <Link
                  key={index}
                  to="/ssu-chatbot"
                  onClick={() => setOpen(false)}
                  className={
                    location.pathname === "/ssu-chatbot" ? styles.active : ""
                  }
                >
                  {page.label}
                </Link>
              );
            }

            return page.hasAccess ? (
              <Link
                key={index}
                to={page.route}
                onClick={() => setOpen(false)}
                className={
                  location.pathname === page.route ? styles.active : ""
                }
              >
                {page.label}
              </Link>
            ) : (
              <span
                key={index}
                className={
                  location.pathname === page.route ? styles.active : ""
                }
                style={{ opacity: 0.6, cursor: "pointer" }}
                onClick={(e) => handleMenuClick(e, page)}
              >
                {page.label}
                <span
                  style={{
                    marginLeft: 6,
                    color: page.comingSoon ? "#ff9800" : "#f44336",
                    fontSize: 12,
                  }}
                >
                  {page.comingSoon ? "*" : "🔒"}
                </span>
              </span>
            );
          })}
          {/* Account section - only for appuser */}
          {normalizedUserType === "USER" && (
            <>
              <div
                className={styles.menuGroupTitle}
                style={{ marginTop: "1.2rem" }}
              >
                Account
              </div>
              <Link
                to="/subscriptions"
                onClick={() => setOpen(false)}
                className={
                  location.pathname === "/subscriptions" ||
                  location.pathname === "/pricing" ||
                  location.pathname === "/plans_and_pricing"
                    ? styles.active
                    : ""
                }
              >
                Subscriptions
              </Link>
              <span
                style={{ opacity: 0.6, cursor: "pointer" }}
                onClick={(e) => {
                  e.preventDefault();
                  setModalMessage(
                    "Profile settings are coming soon and will be available in future updates.",
                  );
                  setModalType("coming-soon");
                  setShowModal(true);
                }}
              >
                Profile{" "}
                <span style={{ marginLeft: 6, color: "#ff9800", fontSize: 12 }}>
                  *
                </span>
              </span>
            </>
          )}
          <Link
            to="/logout"
            onClick={() => setOpen(false)}
            className={location.pathname === "/logout" ? styles.active : ""}
          >
            Logout
          </Link>
        </div>
      )}
      {/* Modal for feature access messages */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "2rem",
              borderRadius: "12px",
              maxWidth: "400px",
              width: "90%",
              textAlign: "center",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            }}
          >
            <div
              style={{
                fontSize: "2rem",
                marginBottom: "1rem",
              }}
            >
              {modalType === "coming-soon" ? "🚧" : "🔒"}
            </div>
            <h3
              style={{
                margin: "0 0 1rem 0",
                color: "#333",
                fontSize: "1.2rem",
              }}
            >
              {modalType === "coming-soon" ? "Coming Soon" : "Premium Feature"}
            </h3>
            <p
              style={{
                margin: "0 0 1.5rem 0",
                color: "#666",
                lineHeight: "1.5",
              }}
            >
              {modalMessage}
            </p>
            <div
              style={{ display: "flex", gap: "1rem", justifyContent: "center" }}
            >
              <button
                onClick={closeModal}
                style={{
                  padding: "0.5rem 1.5rem",
                  border: "1px solid #ddd",
                  background: "#f5f5f5",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
              {modalType === "upgrade-plan" && (
                <button
                  onClick={() => {
                    closeModal();
                    window.location.href = "/subscriptions";
                  }}
                  style={{
                    padding: "0.5rem 1.5rem",
                    border: "none",
                    background: "#00bfae",
                    color: "#fff",
                    borderRadius: "6px",
                    cursor: "pointer",
                  }}
                >
                  Upgrade Plan
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Navbar;
